import { makeStyles } from "@material-ui/core/styles";
import React, { useId } from "react";
import carecentreTokens from "../../tokens";
import { carecentreTypeScale as type } from "../../typeScale";
import { cx } from "../utils";

const { color, radius, space, font } = carecentreTokens;

export const ccFieldZen = color;

/** Matches tokens.css `.cc-field-input` — single-line control height. */
export const CC_INPUT_MIN_HEIGHT = 40;

/** Shared layout: dedicated label above control, hint/error below (PDF / tokens.css). */
export const useCcFieldLayoutStyles = makeStyles(() => ({
  field: {
    display: "flex",
    flexDirection: "column",
    gap: space[2],
    margin: 0,
    width: "100%",
  },
  label: {
    display: "block",
    color: color.textInk,
    fontFamily: font.body,
    fontSize: type.bodySmall.fontSize,
    fontWeight: 700,
    lineHeight: `${type.bodySmall.lineHeight}px`,
    letterSpacing: 0,
  },
  requiredMark: {
    color: color.danger,
    fontWeight: 700,
  },
  hint: {
    margin: 0,
    color: color.textMuted,
    fontFamily: font.body,
    fontSize: type.caption.fontSize,
    lineHeight: `${type.caption.lineHeight}px`,
  },
  error: {
    margin: 0,
    color: color.danger,
    fontFamily: font.body,
    fontSize: type.caption.fontSize,
    lineHeight: `${type.caption.lineHeight}px`,
  },
}));

/** Outlined input shell — no floating MUI label. */
export const useCcInputStyles = makeStyles(() => ({
  root: {
    margin: 0,
    "& .MuiInputLabel-root": {
      display: "none",
    },
    "& .MuiOutlinedInput-root": {
      minHeight: CC_INPUT_MIN_HEIGHT,
      boxSizing: "border-box",
      alignItems: "center",
      "&:not(.MuiOutlinedInput-multiline):not(.MuiAutocomplete-inputRoot)": {
        height: CC_INPUT_MIN_HEIGHT,
        maxHeight: CC_INPUT_MIN_HEIGHT,
        overflow: "hidden",
      },
      borderRadius: radius.md,
      background: color.bgCard,
      color: color.textInk,
      fontFamily: font.body,
      fontSize: type.body.fontSize,
      "& fieldset": {
        borderColor: color.line,
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: color.lineStrong,
      },
      "&.Mui-focused fieldset": {
        borderColor: color.primary,
        borderWidth: 1,
      },
      "&.Mui-error fieldset": {
        borderColor: color.danger,
        borderWidth: 1,
      },
      "&.MuiOutlinedInput-multiline": {
        height: "auto",
        minHeight: "auto",
        alignItems: "flex-start",
      },
    },
    "& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot": {
      padding: `2px ${space[2]}`,
      minHeight: CC_INPUT_MIN_HEIGHT,
      maxHeight: CC_INPUT_MIN_HEIGHT,
      height: CC_INPUT_MIN_HEIGHT,
    },
    "&.cc-input--sm .MuiOutlinedInput-root": {
      minHeight: 32,
      height: 32,
      maxHeight: 32,
      fontSize: type.bodySmall.fontSize,
    },
    "&.cc-input--sm .MuiOutlinedInput-root.MuiAutocomplete-inputRoot": {
      minHeight: 32,
      height: 32,
      maxHeight: 32,
    },
    "&.cc-input--sm .MuiOutlinedInput-root:not(.MuiOutlinedInput-multiline)": {
      height: 32,
      maxHeight: 32,
    },
    "& .MuiAutocomplete-input": {
      padding: `0 ${space[1]} !important`,
      minWidth: 48,
    },
    "& .MuiAutocomplete-tag": {
      height: 24,
      margin: 2,
      "& .MuiChip-label": {
        fontSize: type.caption.fontSize,
        paddingLeft: 6,
        paddingRight: 6,
      },
      "& .MuiChip-deleteIcon": {
        width: 16,
        height: 16,
        margin: "0 2px 0 0",
      },
    },
    "& .MuiOutlinedInput-adornedEnd": {
      paddingRight: space[2],
    },
    "& .MuiOutlinedInput-adornedStart": {
      paddingLeft: space[2],
    },
    "& .MuiInputAdornment-root": {
      height: "auto",
      maxHeight: "100%",
    },
    "& .MuiOutlinedInput-root .MuiIconButton-root": {
      padding: 4,
    },
    "& .MuiOutlinedInput-root .MuiIconButton-root .MuiSvgIcon-root": {
      fontSize: 18,
    },
    "& .MuiOutlinedInput-input:not(.MuiSelect-select):not(.MuiSelect-selectMenu)":
      {
        padding: `0 ${space[3]}`,
        boxSizing: "border-box",
        lineHeight: `${type.body.lineHeight}px`,
        "&::placeholder": {
          color: color.textQuiet,
          opacity: 1,
        },
      },
    "&.cc-select-control .MuiSelect-selectMenu, &.cc-select-control .MuiSelect-select":
      {
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        boxSizing: "border-box",
        lineHeight: 1.25,
        overflow: "hidden",
      },
    "&.cc-select-control .MuiSelect-outlined.MuiOutlinedInput-input": {
      paddingLeft: `${space[3]} !important`,
      paddingRight: `${space[8]} !important`,
      display: "flex",
      alignItems: "center",
    },
    "&.cc-select-control .MuiOutlinedInput-root": {
      height: CC_INPUT_MIN_HEIGHT,
      maxHeight: CC_INPUT_MIN_HEIGHT,
      minHeight: CC_INPUT_MIN_HEIGHT,
      overflow: "hidden",
    },
    "& .MuiOutlinedInput-multiline": {
      padding: 0,
    },
    "& .MuiOutlinedInput-inputMultiline": {
      padding: `${space[3]}`,
      minHeight: 76,
    },
    "& .MuiFormHelperText-root": {
      display: "none",
    },
  },
}));

export const resolveFieldMessage = ({ error, helperText, hint }) => {
  if (typeof error === "string" && error) {
    return { hasError: true, message: error };
  }
  const hasError = Boolean(error);
  const message = hasError ? helperText : helperText || hint;
  return { hasError, message: message || null };
};

const CcFieldLayout = ({
  label,
  required = false,
  hint,
  error,
  helperText,
  htmlFor,
  className,
  children,
}) => {
  const classes = useCcFieldLayoutStyles();
  const autoId = useId();
  const fieldId = htmlFor || autoId;
  const { hasError, message } = resolveFieldMessage({
    error,
    helperText,
    hint,
  });

  const control = React.isValidElement(children)
    ? React.cloneElement(children, {
        id: children.props.id || fieldId,
        error: hasError || Boolean(children.props.error),
        "aria-describedby":
          message &&
          (children.props["aria-describedby"] || `${fieldId}-message`),
      })
    : children;

  return (
    <div className={cx(classes.field, className)}>
      {label ? (
        <label className={classes.label} htmlFor={fieldId}>
          {label}
          {required ? (
            <span className={classes.requiredMark} aria-hidden="true">
              {" "}
              *
            </span>
          ) : null}
        </label>
      ) : null}
      {control}
      {message ? (
        <p
          id={`${fieldId}-message`}
          className={hasError ? classes.error : classes.hint}
          role={hasError ? "alert" : undefined}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
};

export default CcFieldLayout;
